import { dateDbFormat, dateUSFormat } from '../../../utils/dateTimeUtils'
import { privateNoteErrorMessage } from '../../../views/pages/ProgressNote/progressNoteConstants'
import { shouldHidePrivateNote } from '../../../utils/sharedChartFunctions'
import { toast } from 'react-toastify'
import { doCPTsRequireModifier, stringifyCPTCodesNeedingModifier } from './sharedCPTLogic'
import { generateMainDXInvalidMessage, isMainDxInvalid } from './sharedDXLogic'
import { validateCPTDate } from '../../../helper/formater'
import { STATUS_GROUPS, VISIT_STATUS } from '../../../utils/statusConstants'
import { isProcedureValidWithEncounter } from '../../../helper/chartValidationHelper'
import { ENCOUNTER_TYPES } from '../../../views/pages/sharedConstants'

function isDateBeforeToday(visit) {
  return dateDbFormat(visit?.service_date_from) <= dateDbFormat(new Date().toISOString())
}

export function canShowDischarge(visit) {
  return !visit?.statuses_index?.last_visit && isDateBeforeToday(visit)
}
export function canShowUndoDischarge(visit) {
  return visit?.statuses_index?.last_visit && isDateBeforeToday(visit)
}

// Why is this logic the way to determine discharge? No idea.
export function isVisitDischarged(visit) {
  const lastVisitIndex = visit?.statuses?.findIndex((res) => res.key === 'last_visit')

  return (
    lastVisitIndex > -1 &&
    (visit?.statuses[lastVisitIndex]?.value === true ||
      visit?.statuses[lastVisitIndex]?.value === VISIT_STATUS.not_seen)
  )
}

function printToast(msg, isProgressNote) {
  if (isProgressNote) {
    toast.error(msg, {
      position: 'bottom-right',
    })
  } else {
    toast.error(msg)
  }
}

function addCodeValidation(codes, serviceDate) {
  return codes?.map((item) => {
    return {
      ...item,
      isValid: validateCPTDate(item, serviceDate),
    }
  })
}

function checkCodeValidation(codes, serviceDate) {
  let validatedCodes = addCodeValidation(codes, serviceDate)

  let isValid = validatedCodes?.find((item) => item.isValid === false)

  return !Boolean(isValid)
}

function getInValidCodes(codes, serviceDate) {
  let validatedCodes = addCodeValidation(codes, serviceDate)

  return validatedCodes?.filter((item) => item.isValid === false)
}

export function billVisitErrorCheck({
  chart,
  chartValidations,
  diagnosisCodes,
  encounterType,
  isProgressNote = false,
  procedureCodes = [],
  practice,
  provider,
  roles = [],
  service_date_from,
  userId,
}) {
  if (shouldHidePrivateNote(chart, userId)) {
    toast.info(privateNoteErrorMessage)
    return true
  }

  const medicalCodingBypass =
    practice?.medical_coding &&
    provider?.medical_coding !== false &&
    !roles.includes('Claimocity App Biller')

  if (!medicalCodingBypass && doCPTsRequireModifier({ procedureCodes })) {
    let errorMsg = `Error: Procedure Code(s) ${stringifyCPTCodesNeedingModifier(
      procedureCodes,
    )} require a modifier.`

    printToast(errorMsg, isProgressNote)
    return true
  }

  if (procedureCodes.length === 0 && !medicalCodingBypass) {
    printToast(
      `Please add E&M Code and any billable procedures codes to bill the visit`,
      isProgressNote,
    )
    return true
  }

  if (
    // if medical coding is turned on, ignore this check
    !medicalCodingBypass &&
    !isProcedureValidWithEncounter(encounterType, procedureCodes, chartValidations)
  ) {
    // toast is sent in function
    return true
  }

  if (!checkCodeValidation(procedureCodes, service_date_from)) {
    let errorCodes = getInValidCodes(procedureCodes, service_date_from)?.map((code) => code?.code)
    printToast(
      `Procedure Code(s) ${errorCodes.join(',')} not valid for the date of service.`,
      isProgressNote,
    )
    return true
  }

  if (diagnosisCodes.length === 0 && !medicalCodingBypass) {
    printToast('Please add a Diagnosis Code', isProgressNote)
    return true
  }

  if (isMainDxInvalid(diagnosisCodes)) {
    printToast(generateMainDXInvalidMessage(diagnosisCodes), isProgressNote)
    return true
  }

  if (!checkCodeValidation(diagnosisCodes, service_date_from)) {
    let errorCodes = getInValidCodes(diagnosisCodes, service_date_from)?.map((code) => code?.code)
    printToast(
      `Diagnosis Code(s) ${errorCodes.join(',')} not valid for the date of service.`,
      isProgressNote,
    )
    return true
  }

  if (new Date(dateUSFormat(service_date_from)) > new Date()) {
    printToast(
      'Encounters and progress notes cannot be submitted for a future date of service',
      isProgressNote,
    )
    return true
  }

  if (!encounterType) {
    printToast('Please select an Encounter type', isProgressNote)
    return true
  }

  return false
}

export function isChargeSigned(visit) {
  if (
    visit?.statusGroup === STATUS_GROUPS.completed &&
    visit?.activities &&
    visit?.activities?.length > 0
  ) {
    let bill_at = visit.activities.filter((x) => {
      return x.code === 'charge.Bill submitted' || x.code === 'charge.superbill'
    })
    return bill_at && bill_at.length > 0
  }
  return false
}

export function calculateNewEncounterType(oldEncType) {
  if (!oldEncType) {
    return ENCOUNTER_TYPES.INITIAL
  }
  if (oldEncType === ENCOUNTER_TYPES.INITIAL) {
    return ENCOUNTER_TYPES.SUBSEQUENT
  }
  if (oldEncType === ENCOUNTER_TYPES.DISCHARGE) {
    return ENCOUNTER_TYPES.INITIAL
  }

  return oldEncType
}
